import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  ImageList,
  ImageListItem,
  IconButton,
  Paper,
  useTheme,
  Chip,
  FormHelperText,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  Info as InfoIcon,
  LocationOn as LocationOnIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useLostItem, useUpdateLostItem, useFileUpload } from '../hooks/useApi';
import { useNotification } from '../contexts/NotificationContext';
import StoreSelectImproved from './StoreSelectImproved';

const LostItemEditFormImproved: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { showNotification } = useNotification();

  // フォームデータ
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [foundLocation, setFoundLocation] = useState('');
  const [foundAt, setFoundAt] = useState('');
  const [status, setStatus] = useState('Found');
  const [storeId, setStoreId] = useState('');
  const [notes, setNotes] = useState('');
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  // API呼び出し
  const { data: item, isLoading: isLoadingItem, error: itemError } = useLostItem(id || '');
  const updateLostItemMutation = useUpdateLostItem(id || '');
  const fileUploadMutation = useFileUpload();

  // データの初期化
  useEffect(() => {
    if (item) {
      setName(item.name);
      setDescription(item.description || '');
      setFoundLocation(item.found_location || '');
      setFoundAt(item.found_at ? item.found_at.slice(0, 16) : '');
      setStatus(item.status);
      setStoreId(item.store_id || '');
      setNotes(item.notes || '');
      setMediaUrls(item.media_urls || []);
    }
  }, [item]);

  // 権限チェック
  const canEdit = userProfile && (
    userProfile.roles.includes('admin') ||
    userProfile.roles.includes('staff')
  );

  // ファイル選択処理
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      showNotification('無効なファイルが除外されました（画像ファイル、10MB以下のみ）', 'warning');
    }

    setNewFiles(prev => [...prev, ...validFiles]);

    // プレビューURL生成
    const newPreviewUrls = validFiles.map(file => URL.createObjectURL(file));
    setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
  }, [showNotification]);

  // ファイル削除（新規ファイル）
  const handleRemoveNewFile = useCallback((index: number) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // 既存ファイル削除
  const handleRemoveExistingFile = useCallback((index: number) => {
    setMediaUrls(prev => prev.filter((_, i) => i !== index));
  }, []);

  // フォーム送信
  const handleSubmit = async () => {
    if (!canEdit || !id) {
      showNotification('編集権限がありません', 'error');
      return;
    }

    try {
      let finalMediaUrls = [...mediaUrls];

      // 新しいファイルをアップロード
      if (newFiles.length > 0) {
        const uploadPromises = newFiles.map(async (file) => {
          const result = await fileUploadMutation.mutateAsync([file]);
          return result[0];
        });

        const uploadedUrls = await Promise.all(uploadPromises);
        finalMediaUrls = [...finalMediaUrls, ...uploadedUrls];
      }

      // 送信データの準備とログ出力
      const updateData = {
        name,
        description: description || undefined,
        found_location: foundLocation,
        found_at: foundAt ? foundAt + ':00.000Z' : undefined,
        status,
        notes: notes || undefined,
        media_urls: finalMediaUrls,
        store_id: storeId, // store_idを明示的に追加
      };

      console.log('送信データ:', {
        ...updateData,
        userProfile,
        currentUser: currentUser?.uid,
      });

      // アイテム更新
      await updateLostItemMutation.mutateAsync(updateData);

      showNotification('落とし物が正常に更新されました', 'success');
      navigate(`/items/${id}`);
    } catch (error) {
      console.error('更新エラー:', error);
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      showNotification(`更新に失敗しました: ${errorMessage}`, 'error');
    }
  };

  // ローディング状態
  if (isLoadingItem) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // エラー状態
  if (itemError) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          落とし物の情報を取得できませんでした
        </Alert>
      </Container>
    );
  }

  // アイテムが見つからない
  if (!item) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          指定された落とし物が見つかりません
        </Alert>
      </Container>
    );
  }

  // 編集権限がない
  if (!canEdit) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          この落とし物を編集する権限がありません
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* パンくずリスト */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" underline="hover">
          ホーム
        </Link>
        <Link component={RouterLink} to="/items" underline="hover">
          落とし物一覧
        </Link>
        <Link component={RouterLink} to={`/items/${id}`} underline="hover">
          {item.name}
        </Link>
        <Typography color="text.primary">編集</Typography>
      </Breadcrumbs>

      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/items/${id}`)}
          variant="outlined"
        >
          戻る
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            落とし物の編集
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ID: {id}
          </Typography>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* 基本情報 */}
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon />
              基本情報
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                required
                label="品名"
                value={name}
                onChange={(e) => setName(e.target.value)}
                helperText="落とし物の名称を入力してください"
              />

              <FormControl fullWidth required>
                <InputLabel>ステータス</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  label="ステータス"
                >
                  <MenuItem value="Found">未引き取り</MenuItem>
                  <MenuItem value="Claimed">引き取り済み</MenuItem>
                  <MenuItem value="Returned">返却完了</MenuItem>
                  <MenuItem value="Disposed">処分済み</MenuItem>
                </Select>
                <FormHelperText>落とし物の現在の状態</FormHelperText>
              </FormControl>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="詳細説明"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              helperText="落とし物の詳細な説明を入力してください"
            />

            {/* 発見情報 */}
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <LocationOnIcon />
              発見情報
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                required
                label="発見場所"
                value={foundLocation}
                onChange={(e) => setFoundLocation(e.target.value)}
                helperText="落とし物が発見された場所"
              />

              <TextField
                fullWidth
                required
                type="datetime-local"
                label="発見日時"
                value={foundAt}
                onChange={(e) => setFoundAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="落とし物が発見された日時"
              />
            </Box>

            {/* 店舗選択（管理者・スタッフのみ） */}
            {userProfile && (userProfile.roles.includes('admin') || userProfile.roles.includes('staff')) && (
              <>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <InfoIcon />
                  店舗情報
                </Typography>

                <StoreSelectImproved
                  value={storeId || ''}
                  onChange={(newStoreId) => setStoreId(newStoreId)}
                  label="店舗"
                  placeholder="店舗を検索して選択してください"
                  allowManualInput={userProfile.roles.includes('admin')}
                  showMap={true}
                  helperText={userProfile.store_id ? "この落とし物を管理する店舗を選択してください" : "ユーザーの店舗情報が設定されていません。管理者に連絡してください。"}
                />

                {!userProfile.store_id && userProfile.roles.includes('staff') && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    スタッフユーザーの店舗情報が設定されていません。システム管理者にお問い合わせください。
                  </Alert>
                )}
              </>
            )}

            {/* 備考 */}
            <TextField
              fullWidth
              multiline
              rows={2}
              label="備考"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              helperText="追加の情報や注意事項があれば入力してください"
            />

            {/* 既存画像 */}
            {mediaUrls.length > 0 && (
              <>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <PhotoCameraIcon />
                  現在の画像
                </Typography>

                <ImageList cols={3} gap={8}>
                  {mediaUrls.map((url, index) => (
                    <ImageListItem key={url} sx={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`画像 ${index + 1}`}
                        loading="lazy"
                        style={{ borderRadius: theme.shape.borderRadius }}
                      />
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'rgba(255, 255, 255, 0.8)',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                        }}
                        size="small"
                        onClick={() => handleRemoveExistingFile(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ImageListItem>
                  ))}
                </ImageList>
              </>
            )}

            {/* 新しい画像追加 */}
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CloudUploadIcon />
              画像を追加
            </Typography>

            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Button
                component="label"
                variant="contained"
                startIcon={<PhotoCameraIcon />}
                sx={{ mb: 2 }}
              >
                画像を選択
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                画像ファイル（JPEG、PNG、WebP）、10MB以下
              </Typography>
            </Paper>

            {/* 新しい画像のプレビュー */}
            {previewUrls.length > 0 && (
              <ImageList cols={3} gap={8}>
                {previewUrls.map((url, index) => (
                  <ImageListItem key={url} sx={{ position: 'relative' }}>
                    <img
                      src={url}
                      alt={`新しい画像 ${index + 1}`}
                      loading="lazy"
                      style={{ borderRadius: theme.shape.borderRadius }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                      }}
                      size="small"
                      onClick={() => handleRemoveNewFile(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <Chip
                      label="新規"
                      size="small"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        bottom: 4,
                        left: 4,
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}

            {/* アクションボタン */}
            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={updateLostItemMutation.isPending || fileUploadMutation.isPending}
                sx={{ minWidth: 120 }}
              >
                {(updateLostItemMutation.isPending || fileUploadMutation.isPending) ? (
                  <CircularProgress size={20} />
                ) : (
                  '更新'
                )}
              </Button>

              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate(`/items/${id}`)}
                disabled={updateLostItemMutation.isPending || fileUploadMutation.isPending}
              >
                キャンセル
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default LostItemEditFormImproved;
